import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Tooltip
} from "@mui/material";
import { formatBRL, formatNumber } from "../metrics/utils/formatters";

export default function CompetitorTable({ productData }) {
  const getPositionChip = (position) => {
    if (position === 1) {
      return <Chip label="1º" color="success" size="small" sx={{ fontWeight: 600 }} />;
    }
    if (position === 2) {
      return <Chip label="2º" color="warning" size="small" sx={{ fontWeight: 600 }} />;
    }
    if (position === 3) {
      return <Chip label="3º" color="error" size="small" sx={{ fontWeight: 600 }} />;
    }
    return <Chip label={`${position}º`} variant="outlined" size="small" />;
  };


  // Processar dados do metadata.graph
  const competitorsData = (productData.competitors || []).map((competitor, index) => ({
    position: 0,
    seller_id: competitor._id || `Competidor ${index + 1}`,
    seller_nickname: `Vendedor ${index + 1}`,
    price: { amount: competitor.price?.amount || 0 },
    shipping_cost: { amount: 0 }, // Não disponível no graph
    sales_count: competitor.info?.sold_quantity || 0,
    is_current: false,
    title: competitor.info?.title || 'Produto sem título'
  }));

  const tableData = [
    {
      position: 0,
      seller_id: 'Nosso Produto',
      seller_nickname: 'Nossa Empresa',
      price: { amount: productData.current_price?.amount || 0 },
      shipping_cost: { amount: 0 },
      sales_count: '-',
      is_current: true,
      title: productData.product_title || 'Nosso Produto'
    },
    ...competitorsData
  ].sort((a, b) => (a.price?.amount || 0) - (b.price?.amount || 0))
   .map((item, index) => ({ ...item, position: index + 1 }));

  if (!competitorsData || competitorsData.length === 0) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        py: 8,
        color: '#64748b' 
      }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Sem dados de competidores
        </Typography>
        <Typography variant="body2">
          Não há informações de competidores disponíveis para este produto
        </Typography>
      </Box>
    );
  }

  return (
    <Card sx={{
      boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)',
      borderRadius: '16px',
      border: '1px solid rgba(0,0,0,0.06)'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: '#1a1a1a',
            mb: 3
          }}
        >
          Tabela de Competidores
        </Typography>
        
        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: 'none',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem' }}>
                  Posição
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem' }}>
                  Vendedor
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem' }}>
                  Preço
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem' }}>
                  Frete
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem' }}>
                  Total
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem' }}>
                  Vendas
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((competitor, index) => {
                const totalPrice = (competitor.price?.amount || 0) + (competitor.shipping_cost?.amount || 0);
                
                return (
                  <TableRow 
                    key={competitor.seller_id || index}
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8fafc' },
                      '&:nth-of-type(even)': { backgroundColor: '#fefefe' },
                      backgroundColor: competitor.is_current ? 'rgba(255, 112, 67, 0.05)' : 'transparent'
                    }}
                  >
                    <TableCell>
                      {getPositionChip(competitor.position)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            backgroundColor: competitor.is_current ? '#ff7043' : '#e2e8f0',
                            color: competitor.is_current ? 'white' : '#64748b',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          {(competitor.seller_nickname || competitor.seller_id || 'C').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                            {competitor.seller_nickname || competitor.seller_id || 'Vendedor Desconhecido'}
                          </Typography>
                          {competitor.title && !competitor.is_current && (
                            <Tooltip title={competitor.title} arrow placement="top">
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#64748b', 
                                  fontSize: '0.7rem',
                                  cursor: 'help',
                                  '&:hover': { color: '#475569' }
                                }}
                              >
                                {competitor.title.length > 30 ? `${competitor.title.substring(0, 30)}...` : competitor.title}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        sx={{ 
                          fontWeight: 600, 
                          fontSize: '0.875rem',
                          color: competitor.is_current ? '#ff7043' : '#1a1a1a'
                        }}
                      >
                        {formatBRL(competitor.price?.amount || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', color: '#64748b' }}>
                        {competitor.shipping_cost?.amount 
                          ? formatBRL(competitor.shipping_cost.amount)
                          : 'Grátis'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        {formatBRL(totalPrice)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', color: '#64748b' }}>
                        {competitor.sales_count === '-' 
                          ? '-'
                          : formatNumber(competitor.sales_count || 0, 0)
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Legenda */}
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2, 
          mt: 3,
          p: 2,
          backgroundColor: '#f8fafc',
          borderRadius: '8px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              backgroundColor: '#ff7043',
              borderRadius: '2px' 
            }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#64748b' }}>
              Nosso Produto
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}